import { type Dtos, type SuretyAccountId } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../utils/request";
import { incomeStatementsQueryKey } from "./financials.keys";
import { patchIncomeStatementView } from "./utils/patchIncomeStatementView";

export function useDeleteIncomeStatementLineItems(accountId: SuretyAccountId) {
	const { _delete } = useRequest();
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationKey: ["deleteIncomeStatementLineItems", accountId],
		mutationFn: (body: Dtos.Admin.IncomeStatement.BulkDelete.Request) =>
			_delete(`/v2/admin/surety/accounts/${accountId}/income-statements/line-items`, body, "none"),
		onMutate: async (body) => {
			await queryClient.cancelQueries({ queryKey: incomeStatementsQueryKey(accountId) });
			const previousData = queryClient.getQueryData<Dtos.Admin.IncomeStatement.List.Response>(
				incomeStatementsQueryKey(accountId),
			);
			const patchBody: Dtos.Admin.IncomeStatement.Update.Request = {
				field: body.field,
				label: body.label,
				values: { value: null },
			};
			queryClient.setQueryData<Dtos.Admin.IncomeStatement.List.Response>(
				incomeStatementsQueryKey(accountId),
				(cached) =>
					cached?.map((statement) =>
						body.incomeStatementIds.includes(statement.id) && statement.view
							? { ...statement, view: patchIncomeStatementView(statement.view, patchBody) }
							: statement,
					),
			);
			return { previousData };
		},
		onError: (_error, _vars, context) => {
			if (context?.previousData) {
				queryClient.setQueryData(incomeStatementsQueryKey(accountId), context.previousData);
			}
		},
		onSettled: () => {
			if (
				queryClient.isMutating({
					mutationKey: ["deleteIncomeStatementLineItems", accountId],
				}) <= 1
			) {
				queryClient.invalidateQueries({ queryKey: incomeStatementsQueryKey(accountId) });
			}
		},
	});

	return {
		deleteIncomeStatementLineItems: mutation.mutateAsync,
	};
}
